import { Box, Flex } from '@sequencehq/core-components'
import {
  flatFeeLabel,
  getHighestPrecision,
  hasNonZeroFlatFee,
  moneyRangeLabel,
  sanitizeTiersAndAddLowerBounds,
  seatMinimumLabel,
  tierRangeLabel,
  toMoney,
  toPercentage
} from '@sequencehq/utils'
import { GreyGrey50, GreyGrey70 } from '@sequencehq/core-components/theme'
import { match, Pattern } from 'ts-pattern'
import { Price } from 'modules/Cube/domain/cube.domain.types'
import { ReactNode } from 'react'

const TieredSecondaryRows = ({
  tiers,
  currency
}: {
  tiers: NonNullable<
    Extract<
      Price['structure'],
      { pricingType: 'GRADUATED' | 'VOLUME' | 'SEAT_BASED' }
    >['tiers']
  >
  currency: Price['currency']
}) => {
  const sanitized = sanitizeTiersAndAddLowerBounds(tiers)
  const unitPricePrecision = getHighestPrecision(
    sanitized.tiers.map(tier => tier.unitPrice)
  )
  const percentagePrecision = getHighestPrecision(
    sanitized.percentageTiers.map(tier => tier.unitPercentage)
  )

  const rows =
    sanitized.usageTierType === 'FIXED'
      ? sanitized.tiers.map((tier, idx) => (
          <Flex key={idx} color={GreyGrey50} justifyContent="flex-end">
            <Box marginRight="4px" textAlign="right">
              {tierRangeLabel(tier.firstUnit, tier.lastUnit)}
            </Box>
            (
            <Box color={GreyGrey70}>
              {toMoney({
                value: tier.unitPrice,
                currency,
                maximumFractionDigits: unitPricePrecision,
                minimumFractionDigits: unitPricePrecision
              })}
              /unit
              {hasNonZeroFlatFee(tier.flatFee) &&
                flatFeeLabel(tier.flatFee, currency)}
            </Box>
            )
          </Flex>
        ))
      : sanitized.percentageTiers.map((tier, idx) => (
          <Flex key={idx} color={GreyGrey50} justifyContent="flex-end">
            <Box marginRight="4px" textAlign="right">
              {moneyRangeLabel(tier.firstUnit, tier.lastUnit, currency)}
            </Box>
            (
            <Box color={GreyGrey70}>
              {toPercentage(tier.unitPercentage, {
                minimumFractionDigits: percentagePrecision
              })}
              {hasNonZeroFlatFee(tier.flatFee) &&
                flatFeeLabel(tier.flatFee, currency)}
            </Box>
            )
          </Flex>
        ))

  return <>{rows}</>
}

const SeatMinimumRow = ({ seats }: { seats: number }) => (
  <Box color={GreyGrey50} textAlign="right">
    {seatMinimumLabel(seats)}
  </Box>
)

export const renderPriceSecondaryRow = ({
  structure,
  currency
}: {
  structure: Price['structure']
  currency: Price['currency']
}): ReactNode => {
  return match(structure)
    .with(
      {
        pricingType: 'SEAT_BASED',
        tiers: Pattern.when(arr => (arr?.length ?? 0) > 0)
      },
      seatStructure => {
        const seats = seatStructure.contractedMinimumSeats ?? 0
        return (
          <>
            <TieredSecondaryRows
              tiers={seatStructure.tiers ?? []}
              currency={currency}
            />
            {seats > 0 && <SeatMinimumRow seats={seats} />}
          </>
        )
      }
    )
    .with({ pricingType: 'SEAT_BASED' }, seatStructure => {
      const seats = seatStructure.contractedMinimumSeats ?? 0
      return seats > 0 ? <SeatMinimumRow seats={seats} /> : null
    })
    .with(
      {
        pricingType: 'GRADUATED',
        tiers: Pattern.when(arr => (arr?.length ?? 0) > 0)
      },
      {
        pricingType: 'VOLUME',
        tiers: Pattern.when(arr => (arr?.length ?? 0) > 0)
      },
      tieredStructure => (
        <TieredSecondaryRows
          tiers={tieredStructure.tiers ?? []}
          currency={currency}
        />
      )
    )
    .otherwise(() => null)
}
